import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { supplierPortalApi } from '@/api/supplierPortal'
import { AppShell, ConnectionBadge } from '@/components/AppShell'
import { toast } from '@/lib/toast'
import { IconUsers } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { formatDate } from '@/features/orders/status'

/** Πελάτες και αιτήματα σύνδεσης, πλευρά προμηθευτή (§11, οθόνη 3). */
export default function SupplierCustomersPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const [inviting, setInviting] = useState(false)

  const customers = useQuery({ queryKey: ['supplier-customers'], queryFn: supplierPortalApi.customers })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['supplier-customers'] })

  const accept = useMutation({
    mutationFn: supplierPortalApi.acceptConnection,
    onSuccess: () => {
      void invalidate()
      toast.success(t('portal.accepted'))
    },
  })

  const pending = customers.data?.filter((c) => c.status === 'pending_supplier') ?? []

  return (
    <AppShell
      title={t('nav.customers')}
      action={!inviting ? { label: t('portal.invite'), onClick: () => setInviting(true) } : null}
    >
      {inviting && <InviteForm onDone={() => setInviting(false)} />}

      {customers.isPending && <SkeletonList rows={3} />}
      {customers.isError && <Alert>{translateError(customers.error)}</Alert>}

      {pending.length > 0 && (
        <section className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h2 className="mb-2 font-semibold text-amber-900">
            {t('portal.pendingRequests', { count: pending.length })}
          </h2>
          <ul className="flex flex-col gap-2">
            {pending.map((customer) => (
              <li
                key={customer.connectionId}
                className="flex items-center justify-between gap-3 rounded-xl bg-white p-3"
              >
                <span className="min-w-0 truncate font-medium text-gray-900">
                  {customer.businessName}
                </span>
                <div className="w-auto shrink-0">
                  <Button
                    loading={accept.isPending}
                    onClick={() => accept.mutate(customer.connectionId)}
                  >
                    {t('portal.accept')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {customers.data?.length === 0 && (
        <EmptyState
          icon={<IconUsers size={26} />}
          title={t('portal.noCustomers')}
          description={t('portal.noCustomersHint')}
        />
      )}

      <ul className="flex flex-col gap-2">
        {customers.data?.map((customer) => (
          <li
            key={customer.connectionId}
            className="rounded-2xl border border-gray-200 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-semibold text-gray-900">{customer.businessName}</p>
                {(customer.city || customer.phone) && (
                  <p className="text-sm text-gray-500">
                    {[customer.city, customer.phone].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <ConnectionBadge status={customer.status} />
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {t('portal.orderCount', { count: customer.orderCount })}
              {customer.lastOrderAt && ` · ${t('portal.lastOrder')} ${formatDate(customer.lastOrderAt)}`}
            </p>
          </li>
        ))}
      </ul>
    </AppShell>
  )
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const [raw, setRaw] = useState('')
  const [message, setMessage] = useState('')

  const emails = raw
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter((value) => value.includes('@'))

  const invite = useMutation({
    mutationFn: () => supplierPortalApi.invite(emails, message.trim() || undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supplier-customers'] })
      toast.success(t('portal.invitesSent', { count: emails.length }))
      onDone()
    },
  })

  return (
    <form
      className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (emails.length) invite.mutate()
      }}
    >
      <h2 className="font-semibold text-gray-900">{t('portal.invite')}</h2>
      {invite.isError && <Alert>{translateError(invite.error)}</Alert>}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="emails" className="text-sm font-medium text-gray-700">
          {t('portal.inviteEmails')}
        </label>
        <textarea
          id="emails"
          rows={3}
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          placeholder="pelatis1@example.gr, pelatis2@example.gr"
          className="rounded-xl border border-gray-300 bg-white p-3 text-base text-gray-900 outline-none focus:border-brand-600"
        />
        <p className="text-xs text-gray-500">
          {t('portal.inviteCount', { count: emails.length })}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="message" className="text-sm font-medium text-gray-700">
          {t('portal.inviteMessage')}
        </label>
        <textarea
          id="message"
          rows={2}
          maxLength={500}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          className="rounded-xl border border-gray-300 bg-white p-3 text-base text-gray-900 outline-none focus:border-brand-600"
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" loading={invite.isPending} disabled={!emails.length}>
          {t('portal.sendInvites')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
